import React, { useState } from 'react';
import { Form, Row, Col, Button, Table } from 'react-bootstrap';
import { IContact } from '../index';
import styles from './ContactsTab.module.css';
import { DEPARTAMENTOS } from '../../../constants/departamentos';
import { FiSave, FiPlus, FiX, FiEdit2, FiTrash2 } from '../../../utils/icons';
import { useT } from '../../../i18n';

interface ContactsTabProps {
  contacts: IContact[];
  setContacts: React.Dispatch<React.SetStateAction<IContact[]>>;
}

const initialContactState = {
  id: 0,
  nome: '',
  email: '',
  telefone: '',
  departamento: '',
  cargo: '',
};

const ContactsTab: React.FC<ContactsTabProps> = ({ contacts, setContacts }) => {
  const t = useT();
  const [newContact, setNewContact] = useState<Omit<IContact, 'id'>>(initialContactState);
  const [editingId, setEditingId] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewContact(prev => ({ ...prev, [name]: value }));
  };

  const handleAddContact = () => {
    if (newContact.nome && newContact.email) {
      if (editingId !== null) {
        setContacts(prev => prev.map(c => c.id === editingId ? { ...newContact, id: editingId } : c));
        setEditingId(null);
      } else {
        setContacts(prev => [...prev, { ...newContact, id: Date.now() }]);
      }
      setNewContact(initialContactState);
    }
  };

  const handleEditContact = (contact: IContact) => {
    const { id, ...rest } = contact;
    setNewContact(rest);
    setEditingId(id);
  };

  const handleCancelEdit = () => {
    setNewContact(initialContactState);
    setEditingId(null);
  };

  const handleRemoveContact = (id: number) => {
    setContacts(prev => prev.filter(contact => contact.id !== id));
    if (editingId === id) {
      setNewContact(initialContactState);
      setEditingId(null);
    }
  };

  return (
    <div>
      <Form as={Row} className={`g-3 ${styles.formRow}`}>
        <Col md={6}>
          <Form.Group controlId="contactNome">
            <Form.Label>{t('companyRegistration.contacts.nome')}</Form.Label>
            <Form.Control type="text" name="nome" value={newContact.nome} onChange={handleInputChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="contactEmail">
            <Form.Label>{t('companyRegistration.contacts.email')}</Form.Label>
            <Form.Control type="email" name="email" value={newContact.email} onChange={handleInputChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="contactTelefone">
            <Form.Label>{t('companyRegistration.contacts.telefone')}</Form.Label>
            <Form.Control type="tel" name="telefone" value={newContact.telefone} onChange={handleInputChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="contactDepartamento">
            <Form.Label>{t('companyRegistration.contacts.departamento')}</Form.Label>
            <Form.Select name="departamento" value={newContact.departamento} onChange={handleInputChange}>
              <option value="">{t('companyRegistration.contacts.selecione')}</option>
              {DEPARTAMENTOS.map(d => (
                <option key={d} value={d}>{t(`departamento.${d}`)}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="contactCargo">
            <Form.Label>{t('companyRegistration.contacts.cargo')}</Form.Label>
            <Form.Control type="text" name="cargo" value={newContact.cargo} onChange={handleInputChange} />
          </Form.Group>
        </Col>
        <Col md={12} className={`${styles.buttonContainer} d-flex gap-2`}>
          <Button variant="primary" onClick={handleAddContact}>
            {editingId !== null ? <><FiSave /> {t('companyRegistration.contacts.saveChanges')}</> : <><FiPlus /> {t('companyRegistration.contacts.addContact')}</>}
          </Button>
          {editingId !== null && (
            <Button variant="secondary" onClick={handleCancelEdit}><FiX /> {t('companyRegistration.contacts.cancel')}</Button>
          )}
        </Col>
      </Form>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>{t('companyRegistration.contacts.nome')}</th>
            <th>{t('companyRegistration.contacts.email')}</th>
            <th>{t('companyRegistration.contacts.telefone')}</th>
            <th>{t('companyRegistration.contacts.departamento')}</th>
            <th>{t('companyRegistration.contacts.cargo')}</th>
            <th>{t('companyRegistration.contacts.tableActions')}</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map(contact => (
            <tr key={contact.id}>
              <td>{contact.nome}</td>
              <td>{contact.email}</td>
              <td>{contact.telefone}</td>
              <td>{contact.departamento}</td>
              <td>{contact.cargo}</td>
              <td className="d-flex gap-1">
                <Button variant="outline-primary" size="sm" onClick={() => handleEditContact(contact)}><FiEdit2 /> {t('companyRegistration.contacts.edit')}</Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveContact(contact.id)}><FiTrash2 /> {t('companyRegistration.contacts.remove')}</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ContactsTab;